/**
 * The preview of an import into the dictionary (I-3024): the browser parses a
 * file (`dictionaryFile.ts`), compares its rows with the layer that is written
 * and says what each row would do — new, replaced, unchanged or refused, with
 * the reason — before anything is sent. What is not refused becomes ONE merge
 * patch with `origin: "import"`; a patch that would take the layer's document
 * past master's limit of 1 MiB is refused whole, so nothing is written.
 * Master checks the same rules (word rule, fold twins, a term in one group);
 * this only keeps a refused row from reaching it. Pure functions.
 */
import { applyMergePatch, isJsonObject, type JsonObject } from '../config/mergePatch';
import { dictionaryFold } from './dictionaryFold';
import {
  DICTIONARY_DOCUMENT_MAX_BYTES,
  dictionaryDocumentBytes,
  type CorrectionRow,
  type DictionaryLanguage,
  type DictionaryRowProblem,
  type SynonymRow,
} from './dictionaryFile';

/** What a row of the file would do. */
export type DictionaryImportStatus = 'new' | 'replaced' | 'unchanged' | 'refused';

/** Why a row is refused: the file reader's problems and the layer's rules. */
export type DictionaryImportRefusal =
  | DictionaryRowProblem
  | 'same_word'
  | 'fold_twin'
  | 'term_in_two_groups'
  | 'too_large';

export interface DictionaryImportRow {
  /** 1-based line of the file (0 for a word the reader had refused already). */
  line: number;
  /** The wrong word, the group's name or the word. */
  key: string;
  lang: DictionaryLanguage;
  status: DictionaryImportStatus;
  reason?: DictionaryImportRefusal;
  /** The entry that makes the row a twin, or the group that holds the term. */
  other?: string;
}

export interface DictionaryImportPreview {
  rows: DictionaryImportRow[];
  counts: Record<DictionaryImportStatus, number>;
  /** ONE merge patch for the rows that are new or replaced; undefined when there are none or the layer would be too large. */
  patch?: JsonObject;
  /** The layer's document size after the patch, in bytes. */
  bytes: number;
  /** The document would pass master's limit: the whole import is refused. */
  tooLarge: boolean;
}

function mapOf(document: unknown, field: string, lang: DictionaryLanguage): JsonObject {
  const map = isJsonObject(document) ? document[field] : undefined;
  const inner = isJsonObject(map) ? map[lang] : undefined;
  return isJsonObject(inner) ? inner : {};
}

/** Puts one entry into the patch `{ field: { lang: { key: value } } }`. */
function put(patch: JsonObject, field: string, lang: DictionaryLanguage, key: string, value: unknown): void {
  const byLanguage = (patch[field] ??= {}) as JsonObject;
  ((byLanguage[lang] ??= {}) as JsonObject)[key] = value;
}

/** The result of a run over the rows: the size check and the counts, the same for every kind of row. */
function finish(
  rows: DictionaryImportRow[],
  patch: JsonObject,
  ownDocument: unknown,
  extraRefused = 0,
): DictionaryImportPreview {
  const changes = rows.some((row) => row.status === 'new' || row.status === 'replaced');
  const bytes = dictionaryDocumentBytes(changes ? applyMergePatch(ownDocument, patch) : ownDocument);
  const tooLarge = changes && bytes > DICTIONARY_DOCUMENT_MAX_BYTES;
  const settled = tooLarge
    ? rows.map((row): DictionaryImportRow =>
        row.status === 'new' || row.status === 'replaced'
          ? { ...row, status: 'refused', reason: 'too_large' }
          : row,
      )
    : rows;
  const counts: Record<DictionaryImportStatus, number> = { new: 0, replaced: 0, unchanged: 0, refused: extraRefused };
  for (const row of settled) counts[row.status] += 1;
  return {
    rows: settled,
    counts,
    ...(changes && !tooLarge ? { patch } : {}),
    bytes,
    tooLarge,
  };
}

/** What a patch does to the size of the layer's document of a key. */
export interface DictionaryPatchSize {
  /** The document's size after the patch, in bytes. */
  bytes: number;
  /** Whole percent of master's limit (1 MiB) the document would fill. */
  percent: number;
  /** The document would pass the limit: master refuses the write, so it is not sent. */
  tooLarge: boolean;
}

/**
 * The size check every import makes before it sends anything — the words
 * of „Eigene Wörter“ (a plain patch, no row preview) like the previews of the
 * other tabs: the layer's own document of the key with the patch applied
 * against master's limit of 1 MiB.
 */
export function dictionaryPatchSize(ownDocument: unknown, patch: object): DictionaryPatchSize {
  const bytes = dictionaryDocumentBytes(applyMergePatch(ownDocument, patch as JsonObject));
  return {
    bytes,
    percent: Math.round((bytes / DICTIONARY_DOCUMENT_MAX_BYTES) * 100),
    tooLarge: bytes > DICTIONARY_DOCUMENT_MAX_BYTES,
  };
}

/** The folds of a language map's keys, to find a twin by. */
function foldsOf(map: JsonObject): Map<string, string> {
  const folds = new Map<string, string>();
  for (const key of Object.keys(map)) folds.set(dictionaryFold(key), key);
  return folds;
}

/**
 * Corrections (`falsch;richtig;sprache`) against the layer's own document of
 * `spell_check`. A row whose wrong word is a fold twin of another one of the
 * layer or of the file is refused; the same word with the same target is
 * unchanged, with another target replaced; typed = target is refused.
 */
export function correctionsImportPreview(rows: readonly CorrectionRow[], ownDocument: unknown): DictionaryImportPreview {
  const patch: JsonObject = {};
  const out: DictionaryImportRow[] = [];
  const folds = new Map<DictionaryLanguage, Map<string, string>>();
  const accepted = new Map<string, string>();
  for (const row of rows) {
    const base = { line: row.line, key: row.typed, lang: row.lang };
    if (row.problem) {
      out.push({ ...base, status: 'refused', reason: row.problem });
      continue;
    }
    if (dictionaryFold(row.typed) === dictionaryFold(row.to)) {
      out.push({ ...base, status: 'refused', reason: 'same_word' });
      continue;
    }
    const own = mapOf(ownDocument, 'corrections', row.lang);
    const known = folds.get(row.lang) ?? foldsOf(own);
    folds.set(row.lang, known);
    const twin = known.get(dictionaryFold(row.typed));
    if (twin !== undefined && twin !== row.typed) {
      out.push({ ...base, status: 'refused', reason: 'fold_twin', other: twin });
      continue;
    }
    const earlier = accepted.get(`${row.lang}\0${row.typed}`);
    if (earlier !== undefined) {
      out.push(
        earlier === row.to
          ? { ...base, status: 'unchanged' }
          : { ...base, status: 'refused', reason: 'fold_twin', other: row.typed },
      );
      continue;
    }
    const existing = own[row.typed];
    const current = isJsonObject(existing) ? existing.to : undefined;
    known.set(dictionaryFold(row.typed), row.typed);
    accepted.set(`${row.lang}\0${row.typed}`, row.to);
    if (current === row.to) {
      out.push({ ...base, status: 'unchanged' });
      continue;
    }
    put(patch, 'corrections', row.lang, row.typed, { to: row.to, count: 0, origin: 'import' });
    out.push({ ...base, status: existing === undefined ? 'new' : 'replaced' });
  }
  return finish(out, patch, ownDocument);
}

/**
 * Never-suggest words of one language (the word list of the file) against the
 * layer's own document of `spell_check`. `refused` counts the words the file
 * reader had left out already (no word, too long).
 */
export function neverSuggestImportPreview(
  words: readonly string[],
  lang: DictionaryLanguage,
  ownDocument: unknown,
  refused = 0,
): DictionaryImportPreview {
  const patch: JsonObject = {};
  const out: DictionaryImportRow[] = [];
  const own = mapOf(ownDocument, 'never_suggest', lang);
  const known = foldsOf(own);
  words.forEach((word, index) => {
    const base = { line: index + 1, key: word, lang };
    const twin = known.get(dictionaryFold(word));
    if (twin === undefined) {
      known.set(dictionaryFold(word), word);
      put(patch, 'never_suggest', lang, word, { origin: 'import' });
      out.push({ ...base, status: 'new' });
    } else if (twin === word) {
      out.push({ ...base, status: 'unchanged' });
    } else {
      out.push({ ...base, status: 'refused', reason: 'fold_twin', other: twin });
    }
  });
  return finish(out, patch, ownDocument, refused);
}

function sameTerms(a: readonly unknown[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((term, index) => term === b[index]);
}

/**
 * Synonym groups (`name;sprache;richtung;begriffe`) against the layer's own
 * document of `search`. A name that is a fold twin of another is refused, and
 * so is a term another group of the layer or of the file already holds (a
 * replaced group gives its old terms free); equal terms and direction are
 * unchanged. Terms equal by fold within a group count once.
 */
export function synonymsImportPreview(rows: readonly SynonymRow[], ownDocument: unknown): DictionaryImportPreview {
  const patch: JsonObject = {};
  const out: DictionaryImportRow[] = [];
  const names = new Map<DictionaryLanguage, Map<string, string>>();
  const owners = new Map<DictionaryLanguage, Map<string, string>>();
  // The term folds each group holds, so a replaced group frees only its own
  // terms (no scan of every term per row).
  const folds = new Map<DictionaryLanguage, Map<string, string[]>>();
  const accepted = new Set<string>();
  const ownersOf = (lang: DictionaryLanguage): Map<string, string> => {
    let known = owners.get(lang);
    if (!known) {
      known = new Map();
      const byGroup = new Map<string, string[]>();
      for (const [name, group] of Object.entries(mapOf(ownDocument, 'synonyms', lang))) {
        const terms = isJsonObject(group) && Array.isArray(group.terms) ? group.terms : [];
        const held: string[] = [];
        for (const term of terms) {
          if (typeof term !== 'string') continue;
          const key = dictionaryFold(term);
          known.set(key, name);
          held.push(key);
        }
        byGroup.set(name, held);
      }
      owners.set(lang, known);
      folds.set(lang, byGroup);
    }
    return known;
  };
  for (const row of rows) {
    const base = { line: row.line, key: row.name, lang: row.lang };
    if (row.problem) {
      out.push({ ...base, status: 'refused', reason: row.problem });
      continue;
    }
    const own = mapOf(ownDocument, 'synonyms', row.lang);
    const known = names.get(row.lang) ?? foldsOf(own);
    names.set(row.lang, known);
    const twin = known.get(dictionaryFold(row.name));
    if (twin !== undefined && twin !== row.name) {
      out.push({ ...base, status: 'refused', reason: 'fold_twin', other: twin });
      continue;
    }
    const seen = new Set<string>();
    const terms = row.terms.filter((term) => {
      const key = dictionaryFold(term);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (terms.length < 2) {
      out.push({ ...base, status: 'refused', reason: 'terms_count' });
      continue;
    }
    if (accepted.has(`${row.lang}\0${row.name}`)) {
      out.push({ ...base, status: 'refused', reason: 'fold_twin', other: row.name });
      continue;
    }
    const holders = ownersOf(row.lang);
    const clash = terms
      .map((term) => holders.get(dictionaryFold(term)))
      .find((holder) => holder !== undefined && holder !== row.name);
    if (clash !== undefined) {
      out.push({ ...base, status: 'refused', reason: 'term_in_two_groups', other: clash });
      continue;
    }
    const existing = own[row.name];
    const same =
      isJsonObject(existing) &&
      Array.isArray(existing.terms) &&
      sameTerms(existing.terms, terms) &&
      (existing.oneWay === true) === row.oneWay;
    known.set(dictionaryFold(row.name), row.name);
    accepted.add(`${row.lang}\0${row.name}`);
    const byGroup = folds.get(row.lang)!;
    for (const key of byGroup.get(row.name) ?? []) if (holders.get(key) === row.name) holders.delete(key);
    const held = terms.map((term) => dictionaryFold(term));
    for (const key of held) holders.set(key, row.name);
    byGroup.set(row.name, held);
    if (same) {
      out.push({ ...base, status: 'unchanged' });
      continue;
    }
    put(patch, 'synonyms', row.lang, row.name, { terms, oneWay: row.oneWay, origin: 'import' });
    out.push({ ...base, status: existing === undefined ? 'new' : 'replaced' });
  }
  return finish(out, patch, ownDocument);
}
